import { MESSAGE_TEMPLATE_REQUIRED_URL_VARIABLE, MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME } from "@constants/message-template-constants";
import { resources } from "@constants/hardcoded-strings";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    MessageTemplateContentValidationErrors,
    MessageTemplateGeneralInfoValidationErrors,
    MessageTemplateValidationErrors,
    MessageTemplateVariablesMultiLanguageValidationErrors,
    MessageTemplateVariablesValidationErrors
} from "@idw-types/common/validation/message-template/message-template-validation-errors";
import { FormattedMessageTemplate } from "@idw-types/data/message-template";
import { getMessageTemplateVariables } from "@utils/message-template/message-template-util";
import { isNullOrUndefined } from "@utils/object-util";
import { convertJSONObjectToDotSeparatedString } from "@utils/string-util";
import { getErrorAlert } from "@utils/tab-util";
import { MessageTemplateTypes } from "@enums/message-template-types";


export const getMessageTemplateValidationErrors = (messageTemplate: FormattedMessageTemplate): MessageTemplateValidationErrors => {
    const generalInfoErrors = getMessageTemplateGeneralInfoValidationErrors(messageTemplate);
    const contentErrors = getMessageTemplateContentValidationErrors(messageTemplate);
    const variablesErrors = getMessageTemplateVariablesValidationErrors(messageTemplate);

    const count = generalInfoErrors.count + contentErrors.count + variablesErrors.count;

    return {
        count,
        generalInfo: generalInfoErrors,
        variables: variablesErrors,
        content: contentErrors,
        contentAndVariables: {
            count: variablesErrors.count,
            tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messageTemplateDetails.tabs.content.tabName} errorCount={variablesErrors.count} />
        }
    };
};
export const getMessageTemplateContentValidationErrors = (messageTemplate: FormattedMessageTemplate): MessageTemplateContentValidationErrors => {
    let count = 0;
    let requiredVariableError: React.ReactNode = null;

    if (!messageTemplate?.template?.includes(MESSAGE_TEMPLATE_REQUIRED_URL_VARIABLE)) {
        count++;
        requiredVariableError = (
            <ErrorAlert id={`message-template-required-variable-error`}>
                <>
                    <div>{`The following text must appear in your template:`}</div>
                    <p>{MESSAGE_TEMPLATE_REQUIRED_URL_VARIABLE}</p>
                    <div>{`This text will be substituted in the message sent to the user. The user will use the substituted URL.`}</div>
                </>
            </ErrorAlert>
        );
    }
    return {
        count,
        requiredVariableError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messageTemplateDetails.tabs.content.tabName} errorCount={count} />
    };
};

const getMessageTemplateGeneralInfoValidationErrors = (messageTemplate: FormattedMessageTemplate): MessageTemplateGeneralInfoValidationErrors => {
    let nameError: React.ReactNode = null;
    let senderError: React.ReactNode = null;
    let count = 0;
    let companyError = null;
    let templateAvailabilityLevelError = null;
    const { templateAvailabilityLevel, company } = messageTemplate;

    if (!messageTemplate.name) {
        count++;
        nameError = getErrorAlert(resources.common.requiredField);
    }

    if (templateAvailabilityLevel === null) {
        templateAvailabilityLevelError = getErrorAlert(resources.common.requiredField);
        count++;
    } else {
        if (templateAvailabilityLevel === TemplateAvailabilityLevelTypes.ORGANIZATION && company === null) {
            companyError = getErrorAlert(resources.common.requiredField);
            count++;
        }
    }
    if (messageTemplate.sender === "") {
        count += 1;
        senderError = (
            <ErrorAlert>
                <span>{resources.pages.messageTemplateDetails.tabs.variables.defaultEmailSender.error}</span>
            </ErrorAlert>
        );
    }

    return {
        count,
        nameError,
        templateAvailabilityLevelError,
        companyError,
        senderError,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messageTemplateDetails.tabs.generalInfo.tabName} errorCount={count} />
    };
};


export const getMessageTemplateVariablesValidationErrors = (messageTemplate: FormattedMessageTemplate): MessageTemplateVariablesValidationErrors => {
    let count = 0;
    const multiLanguageValidationErrors: MessageTemplateVariablesMultiLanguageValidationErrors = {};

    const availableLanguages = Object.keys(messageTemplate.languages) as Array<LocaleTypes>;
    availableLanguages.forEach(locale => {
        let flattenedLanguageFile: { [key: string]: string } = {};
        if (messageTemplate.languages[locale]) {
            flattenedLanguageFile = convertJSONObjectToDotSeparatedString(messageTemplate.languages[locale]);
        }
        const finalVariables: Array<{ name: string; value: string; }> = getMessageTemplateVariables(messageTemplate.languages, messageTemplate.template, locale);


        multiLanguageValidationErrors[locale] = { errors: {}, count: 0 };

        finalVariables.forEach(variable => {
            if (!variable.value) {
                multiLanguageValidationErrors[locale].errors[variable.name] = getErrorAlert(resources.common.requiredField, `required-variable-${variable.name}`);
                multiLanguageValidationErrors[locale].count += 1;
                count++;
            }
        });

        if (messageTemplate.type === MessageTemplateTypes.EMAIL && isNullOrUndefined(flattenedLanguageFile[MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME]) || flattenedLanguageFile[MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME] === "") {
            multiLanguageValidationErrors[locale].errors[MESSAGE_TEMPLATE_SUBJECT_VARIABLE_NAME] = getErrorAlert(resources.common.requiredField, `required-variable-email.subject`);
            multiLanguageValidationErrors[locale].count += 1;
            count++;
        }
    });


    return {
        count: count,
        multiLanguageValidationErrors,
        tabTitle: <TabTitleWithErrorCount tabName={resources.pages.messageTemplateDetails.tabs.variables.tabName} errorCount={count} />
    };
};